import React, { useState } from "react";
import { X, HeartHandshake, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const AMOUNTS = [
  { label: "$25", value: 25, sub: "1 mother — 1 month of peer-circle support" },
  { label: "$50", value: 50, sub: "Therapy sessions for one mother" },
  { label: "$100", value: 100, sub: "Seed micro-grant kit" },
  { label: "$250", value: 250, sub: "School scholarship — one term" },
];

const METHODS = [
  {
    key: "mpesa",
    name: "M-Pesa Paybill",
    primary: "Paybill: 400200",
    secondary: "Account: NGUVUYAMWANAMKE",
  },
  {
    key: "bank",
    name: "Bank Transfer",
    primary: "Equity Bank — 1234567890",
    secondary: "Nguvu Ya Mwanamke Foundation",
  },
  {
    key: "paypal",
    name: "PayPal",
    primary: "nymfoundationorg@gmail.com",
    secondary: "Tap to copy",
  },
];

export default function DonateDialog({ open, onClose }) {
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [copied, setCopied] = useState("");

  if (!open) return null;

  const copy = async (val, key) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(""), 1600);
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };

  const display = custom ? `$${custom}` : `$${amount}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="donate-dialog"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
    >
      <div
        className="absolute inset-0 bg-brand-navy/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="donate-dialog-backdrop"
      />
      <div className="relative w-full sm:max-w-2xl bg-brand-cream rounded-t-3xl sm:rounded-3xl ring-1 ring-brand-border overflow-hidden shadow-2xl">
        <div className="flex items-start justify-between p-6 pb-2">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold flex items-center gap-2">
              <HeartHandshake className="h-4 w-4" />
              Make a gift
            </div>
            <div className="mt-2 font-serif text-2xl sm:text-3xl text-brand-ink">
              Stand with a mother — {display}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="donate-dialog-close"
            className="h-10 w-10 rounded-full border border-brand-border bg-white text-brand-ink hover:bg-brand-sand transition flex items-center justify-center"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 pb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            {AMOUNTS.map((a) => (
              <button
                key={a.value}
                onClick={() => {
                  setAmount(a.value);
                  setCustom("");
                }}
                data-testid={`donate-amount-${a.value}`}
                className={`rounded-2xl border px-3 py-4 text-left transition-all ${
                  amount === a.value && !custom
                    ? "border-brand-forest bg-brand-forest text-white"
                    : "border-brand-border bg-white hover:border-brand-forest text-brand-ink"
                }`}
              >
                <div className="font-serif text-2xl">{a.label}</div>
                <div
                  className={`mt-1 text-[11px] leading-snug ${
                    amount === a.value && !custom
                      ? "text-white/85"
                      : "text-brand-muted"
                  }`}
                >
                  {a.sub}
                </div>
              </button>
            ))}
          </div>

          <div className="mt-4">
            <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
              Custom amount (USD)
            </label>
            <input
              type="number"
              min={1}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              data-testid="donate-custom-amount"
              placeholder="Enter any amount"
              className="mt-2 w-full rounded-xl border border-brand-border bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
            />
          </div>

          <div className="mt-6">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold">
              Donation methods
            </div>
            <div className="mt-3 space-y-3">
              {METHODS.map((m) => (
                <div
                  key={m.key}
                  data-testid={`donate-method-${m.key}`}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-brand-border bg-white p-4"
                >
                  <div>
                    <div className="text-sm uppercase tracking-[0.16em] text-brand-muted font-semibold">
                      {m.name}
                    </div>
                    <div className="mt-1 font-serif text-lg text-brand-ink">
                      {m.primary}
                    </div>
                    <div className="text-xs text-brand-muted mt-0.5">
                      {m.secondary}
                    </div>
                  </div>
                  <button
                    onClick={() => copy(m.primary, m.key)}
                    data-testid={`donate-copy-${m.key}`}
                    className="inline-flex items-center gap-2 rounded-full border border-brand-forest text-brand-forest px-4 py-2 text-sm font-semibold hover:bg-brand-forest hover:text-white transition"
                  >
                    {copied === m.key ? (
                      <>
                        <CheckCircle2 className="h-4 w-4" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" /> Copy
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-6 text-xs text-brand-muted leading-relaxed">
            Bank, M-Pesa and PayPal details are placeholders — replace them in
            <span className="font-mono"> /src/components/site/DonateDialog.jsx </span>
            with your live accounts. Online card payments can be added via
            Stripe once you share API keys.
          </p>
        </div>
      </div>
    </div>
  );
}
